<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useToast } from 'vue-toastification'
import type { Package, PackageType } from '~/composables/tebex'

const { t } = useI18n()
const { addPackage, removePackage, updatePackage, basket } = useTebex()
const { isExclusive, exceedsExclusive } = useTebexExclusive()
const { period, renewalPrice } = useTebexRenewal()
const { locale } = useI18n()
const toast = useToast()

const props = defineProps<{
  package: Package
  packages: Record<number, Package>
}>()

// true if the tebex login should be shown
const login = ref<boolean>(false)
// the package type that was clicked, used for login and showing loading animation
const pending = ref<PackageType | null>(null)

// called on login success to add the previously clicked package type (see 'pending')
async function retry() {
  login.value = false
  if (!pending.value) {
    return
  }
  await add(pending.value)
}

// cancel any login and pending package add
async function cancel() {
  login.value = false
  pending.value = null
}

// tries to add a package to the basket, opens login if basket is missing
async function add(type: PackageType) {
  pending.value = type
  if (!basket.value) {
    login.value = true
    return
  }

  await addPackage(props.package.id, type)
    .then(() => revertExceeded())
    .catch((e) => {
      console.error('failed to add package to basket', e)
      // tebex explains rejections (e.g. purchase limits or subscription stacking) in its problem details, in english only
      const reason = e?.statusCode < 500 ? e?.data?.detail || e?.data?.title : null
      toast.error(reason ? t('jc.component.tebexButton.rejected', { reason }) : t('jc.component.tebexButton.failed'))
    })
    .finally(() => { pending.value = null })
}

// takes the package out again if premium is now more than once in the basket, as another tab may have added premium
// without this tab knowing
async function revertExceeded() {
  const item = inBasket.value
  if (!exclusive.value || !item || !exceedsExclusive(basket.value, props.packages)) {
    return
  }
  try {
    if (item.in_basket.quantity > 1) {
      await updatePackage(item.id, item.in_basket.quantity - 1)
      toast.error(t('jc.component.tebexButton.inBasket'))
    }
    else {
      await removePackage(item.id)
      toast.error(t('jc.component.tebexButton.otherInBasket'))
    }
  }
  catch (e) {
    // the basket blocks the checkout until premium is only once in it
    console.error('failed to take exclusive package out of basket again', e)
  }
}

const inBasket = computed(() => basket.value?.packages.find(item => item.id == props.package.id))
const exclusive = computed(() => isExclusive(props.package))
const limitReached = computed(() => (props.package.disable_quantity || exclusive.value) && !!inBasket.value)
const otherInBasket = computed(() => exclusive.value && !inBasket.value
  && (basket.value?.packages.some(item => props.packages[item.id]?.category.id == props.package.category.id) ?? false))
// tebex merges both types of a package into the entry that was added first, so the other type can't be added
const inBasketAs = computed<PackageType | null>(() => inBasket.value ? (inBasket.value.is_recurring ? 'subscription' : 'single') : null)

const addDisabled = computed(() => limitReached.value || otherInBasket.value || inBasketAs.value == 'subscription')
const subscribeDisabled = computed(() => limitReached.value || otherInBasket.value || inBasketAs.value == 'single')
// explains the disabled buttons in their tooltip and to screen readers
const note = computed(() => {
  if (inBasketAs.value && props.package.type == 'both') {
    return t(`jc.component.tebexButton.inBasketAs.${inBasketAs.value}`)
  }
  if (limitReached.value) {
    return t('jc.component.tebexButton.inBasket')
  }
  if (otherInBasket.value) {
    return t('jc.component.tebexButton.otherInBasket')
  }
  return null
})
const noteId = useId()

const formatter = computed(() => new Intl.NumberFormat(locale.value || 'de-DE', { style: 'currency', currency: basket.value?.currency ?? 'EUR' }))

// the price above is only the first payment of a subscription during a sale, so say what it renews at
const renewal = computed(() => {
  const every = period(props.package)
  if (props.package.type == 'single' || !every) {
    return null
  }
  const full = formatter.value.format(renewalPrice(props.package))
  return props.package.discount
    ? t('jc.component.tebexButton.renewal.sale', { first: formatter.value.format(props.package.total_price), renewal: full, period: every })
    : t('jc.component.tebexButton.renewal.regular', { renewal: full, period: every })
})
</script>

<template>
  <div>
    <TebexLogin
      :open="login"
      @done="() => retry()"
      @cancel="() => cancel()"
    />

    <div class="bg-secondary flex flex-col gap-2">
      <div class="flex flex-row gap-2 text-white">
        <span>{{ t(`jc.tebex.category.${packages?.[props.package.id]?.category?.id ?? 'fallback'}.packageName`, { amount: props.package.creator_meta_data?.amount ?? 0 }) }}</span>
        <span class="ml-auto line-through">{{ props.package.discount ? formatter.format(props.package.base_price + props.package.discount) : '' }}</span>
        <span class="font-semibold">{{ formatter.format(props.package.total_price) }}</span>
      </div>
      <button
        v-if="props.package.type == 'single' || props.package.type == 'both'"
        type="submit"
        :disabled="pending != null"
        :aria-disabled="addDisabled"
        :aria-describedby="addDisabled && note ? noteId : undefined"
        class="bg-primary hover:bg-primary-highlighted active:bg-primary-selected disabled:opacity-50 aria-disabled:opacity-50 aria-disabled:bg-primary aria-disabled:cursor-default px-3 py-2 rounded text-white flex flex-row gap-4 justify-between group"
        :title="(addDisabled ? note : null) ?? t('jc.component.tebexButton.addButton.title')"
        @click="() => addDisabled || add('single')"
      >
        <font-awesome-icon
          :icon="pending == 'single' ? 'spinner-third' : 'cart-shopping-fast'"
          :spin="pending == 'single'"
          class="text-[1.2em] animate-bounce-once group-active:animate-none group-aria-disabled:animate-none"
        />
        <span>{{ t('jc.component.tebexButton.addButton.label') }}</span>
      </button>
      <button
        v-if="props.package.type == 'subscription' || props.package.type == 'both'"
        type="submit"
        :disabled="pending != null"
        :aria-disabled="subscribeDisabled"
        :aria-describedby="subscribeDisabled && note ? noteId : undefined"
        class="bg-white hover:gray-50 active:gray-100 disabled:opacity-50 aria-disabled:opacity-50 px-3 py-2 rounded text-primary hover:text-primary-highlighted active:text-primary-selected aria-disabled:text-primary aria-disabled:cursor-default flex flex-row gap-4 justify-between"
        :title="(subscribeDisabled ? note : null) ?? t('jc.component.tebexButton.subscribeButton.title')"
        @click="() => subscribeDisabled || add('subscription')"
      >
        <font-awesome-icon
          icon="arrows-rotate"
          :spin="pending == 'subscription'"
          class="text-[1.2em]"
        />
        <span>{{ t('jc.component.tebexButton.subscribeButton.label') }}</span>
      </button>
      <p
        v-if="renewal"
        class="text-xs text-gray-300 text-center"
      >
        {{ renewal }}
      </p>
      <!-- only a description: aria-disabled keeps the buttons focusable, so screen readers announce why they're disabled -->
      <span
        v-if="note"
        :id="noteId"
        hidden
      >{{ note }}</span>
    </div>
  </div>
</template>
